import { useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";

export const DEFAULT_SECTIONS = [
  // dashboardOnly: true = appears in Customise but never in Sidebar / BottomTabBar
  { id: "wakeWindow",         label: "Wake Window Card",         icon: "⏱️", visible: true, dashboardOnly: true },
  { id: "bottleFeeds",        label: "Bottle Feeding",          icon: "🍼", visible: true },
  { id: "breastfeeding",      label: "Breastfeeding",           icon: "🤱", visible: true },
  { id: "diapers",            label: "Nappy Changes",           icon: "🧷", visible: true },
  { id: "sleepTracking",      label: "Sleep Tracking",          icon: "😴", visible: true },
  { id: "waterIntake",        label: "Water Intake",            icon: "💧", visible: true },
  { id: "solidFoods",         label: "Solid Foods",             icon: "🥘", visible: true },
  { id: "foodIntroductions",  label: "Food Introductions",      icon: "🍎", visible: true },
  { id: "growthHealth",       label: "Growth & Health",         icon: "📏", visible: true },
  { id: "milestones",         label: "Milestones",              icon: "🎯", visible: true },
  { id: "milestoneChecklist", label: "NHS Milestone Checklist", icon: "✅", visible: true, fullWidth: true },
  { id: "doctorVisits",       label: "Doctor Visits",           icon: "🏥", visible: true },
  { id: "vaccinations",       label: "Vaccinations",            icon: "💉", visible: true },
  { id: "allergies",          label: "Food Allergies",          icon: "⚠️", visible: true },
  { id: "journal",            label: "Baby Journal",            icon: "📖", visible: true, fullWidth: true },
];

export const useDashboardSettings = (userId) => {
  const [sections, setSections]           = useState(DEFAULT_SECTIONS);
  const [tourCompleted, setTourCompleted] = useState(false);
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }

    getDoc(doc(db, "dashboardSettings", userId))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();

          // Restore tour completion flag
          if (data.tourCompleted) setTourCompleted(true);

          const saved = data.sections || [];
          // Build a lookup: { id -> { visible, order } }
          const savedMap = Object.fromEntries(
            saved.map((s, i) => [s.id, { visible: s.visible, order: i }])
          );
          // Merge: apply saved visibility/order, keep any new defaults at the end
          const merged = DEFAULT_SECTIONS
            .map((def) => ({
              ...def,
              visible: savedMap[def.id]?.visible ?? def.visible,
              _order:  savedMap[def.id]?.order   ?? 999,
            }))
            .sort((a, b) => a._order - b._order)
            .map(({ _order, ...rest }) => rest);

          setSections(merged);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [userId]);

  const saveSettings = useCallback(async (newSections) => {
    setSections(newSections);
    if (!userId) return;
    // merge: true so we don't overwrite tourCompleted or other fields
    await setDoc(
      doc(db, "dashboardSettings", userId),
      { sections: newSections.map(({ id, visible }) => ({ id, visible })) },
      { merge: true }
    );
  }, [userId]);

  const markTourComplete = useCallback(async () => {
    setTourCompleted(true);
    if (!userId) return;
    await setDoc(
      doc(db, "dashboardSettings", userId),
      { tourCompleted: true },
      { merge: true }
    );
  }, [userId]);

  return { sections, saveSettings, loading, tourCompleted, markTourComplete };
};
